import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import Radio from "@mui/material/Radio"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "./components/MDNumberInput"
import { formatNumberInput } from "procurement/design/format/number"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import SectionCard from "./components/SectionCard"
import StatusBadge from "./components/StatusBadge"
import VendorProposalsPanel from "./components/proposals/VendorProposalsPanel"
import DirectSourceBuilder, {
  emptyDirectSourceState,
  computeLinesTotal,
  isDirectSourceValid,
  directSourceErrors,
  hasDirectSourceErrors,
  buildDirectSourcePayload,
} from "./components/DirectSourceBuilder"
import "./ui/tokens.css"
import { Page, Card, Button, Loading, Checkbox } from "./ui"
import {
  useCreatePurchaseMutation,
  useGetPurchaseQuery,
  useUpdatePurchaseMutation,
  useResubmitPurchaseMutation,
  useGetPurchaseSuppliersQuery,
  useGetVendorProposalsQuery,
  useGetOrderPriceReadinessQuery,
  useUpdatePurchaseProposalPricesMutation,
} from "shared/redux/endpoints/procurementPurchases"
import { useGetAllOrderRequestsQuery } from "shared/redux/endpoints/procurementApprovals"
import {
  useGetProcurementSuppliersQuery,
  useCreateProcurementSupplierMutation,
} from "shared/redux/endpoints/procurementItems"
import { useGetVendorProposalPurchaseLinesQuery } from "shared/redux/endpoints/publicVendorProposals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import { RULES, validateFields, hasErrors } from "./fieldRules"
import { ORDER_STATUS, PO_STATUS, canEditPurchase } from "./status"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import {
  PAYMENT_METHOD_OPTIONS,
  formatVND,
  formatDate,
  normalizeOrderOrGroupItem,
  NO_QUOTE_LABEL,
  quotedItems,
  hasQuote,
  proposalTotal,
  toProposalLines,
  proposalLineKey,
  proposalLineRef,
  referencePriceVendorId,
  MISSING_PRICE_REASON_LABEL,
  linePeriodLabel,
  LINE_PERIOD_COLOR,
  isInternalStockSupplier,
} from "./constants"
import InternalStockChoice from "./components/purchase/InternalStockChoice"

const SOURCE = { ORDER: "order", GROUP: "group", DIRECT: "direct" }
const AMOUNT_MODE = { FULL: "full", PROPOSAL: "proposal" }

// What happens to a blank PO-draft row, from the OR's price-readiness preview
// (order source only — there's no group preview).
const draftRowHint = (r, readinessRow) => {
  if (r.priceFrom === "proposal") return "Giá từ báo giá đã chọn"
  if (!readinessRow) return "Để trống: hệ thống tự điền nếu NCC có giá"
  if (readinessRow.status === "AUTO_FILLABLE") return `Để trống: tự điền ${formatVND(readinessRow.suggestedAmount)}`
  if (readinessRow.status === "MISSING")
    return `${readinessRow.message || MISSING_PRICE_REASON_LABEL[readinessRow.missingReason] || "Chưa có giá"} — nhập ở đây hoặc trên PO`
  return null
}

const errorMessage = (e, fallback) => e?.data?.data?.message || e?.data?.message || fallback

// Create a draft PO from exactly ONE source — a single approved order or a
// single group order — optionally split to one chosen vendor proposal's
// amount instead of the source's full total.
// POST /purchases:
//   full amount:   { orderRequestId|groupOrderId, vendorId, paymentMethod }
//   split by quote: { orderRequestId|groupOrderId, vendorProposalId, vendorId }
// A group order bundles several approved orders that must already share the
// same category + region (BE-enforced, 400 otherwise); a single order is
// always homogeneous by definition.
const CreatePurchaseContainer = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectOrderId = searchParams.get("order_id") || null

  // ── Source list — orders and group orders in one place ──────────────────────
  // GET /order-requests returns a MIXED list (item.type "order"|"group"); a
  // group's status/total are already derived server-side from its children,
  // so picking one needs no extra lookup.
  const [selectedKey, setSelectedKey] = useState(
    preselectOrderId ? `order-${preselectOrderId}` : null
  )
  const [expandedGroupKey, setExpandedGroupKey] = useState(null)

  // ── Direct source (2026-09 barrier-removal feature) ─────────────────────────
  // A 3rd way to source a PO, alongside picking an order/group below: build
  // one from scratch (title + budget category + lines) when the category
  // doesn't require an Order first. Always offered — if no category is
  // flagged that way yet, DirectSourceBuilder itself lets the user create one
  // inline (preset with mustHaveOrder=false) instead of dead-ending here.
  const [sourceMode, setSourceMode] = useState("picker") // "picker" | "direct"
  const [directState, setDirectState] = useState(emptyDirectSourceState)
  const {
    data: itemsData,
    isFetching: itemsFetching,
    isError: itemsError,
  } = useGetAllOrderRequestsQuery({
    status: ORDER_STATUS.APPROVED,
    // Server-side filter: hides an order/group only once every line is on a
    // live (non-cancelled) PO, and adds `availableLines` (the free ones) to
    // each item — so one OR can feed several POs, e.g. one per supplier.
    // Only applied here (Create PO); every other order-requests list still
    // sees the full set.
    excludeWithActivePurchase: true,
    // Lets a group order that isn't fully approved yet still appear (as
    // `status=approved`, with `partiallyApproved: true`) so a PO can be
    // started early from just its approved members — creating the PO then
    // splits the rest off the group instead of blocking.
    allowEarlyPurchase: true,
    size: 100,
  })

  useEffect(() => {
    if (!itemsError) return
    showToastError("Couldn't load approved orders/group orders. Please try again.")
  }, [itemsError])

  const items = useMemo(
    () => (itemsData?.items || []).map(normalizeOrderOrGroupItem),
    [itemsData]
  )
  const selectedItem = items.find((it) => `${it.type}-${it.id}` === selectedKey) || null

  // For a partially-approved group, `total` from the list is the FULL group
  // total (every member, approved or not) — but creating a PO from it only
  // pulls in the approved members (the rest get split off), so the PO's real
  // total is the sum of just those. Recompute it client-side for the picker
  // and the PO summary; a fully-approved group's approved-only sum equals its
  // full total anyway, so this is a no-op for the common case.
  const approvedMembersTotal = (it) =>
    (it.orders || [])
      .filter((o) => o.status === ORDER_STATUS.APPROVED)
      .reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0)
  const detachingMembers = (it) => (it.orders || []).filter((o) => o.status !== ORDER_STATUS.APPROVED)

  // ── Lines going on this PO ──────────────────────────────────────────────────
  // `availableLines` = the source's lines not yet on a live PO (a group's are
  // its approved members' lines). Sent as `lineIds`; the PO total follows the
  // ticked lines. Old ORs without lines come back empty → no picker, and the
  // create call omits lineIds (BE then takes every free line).
  const pickerLines = sourceMode === "direct" ? [] : selectedItem?.availableLines || []
  const hasLinePicker = pickerLines.length > 0
  const [selectedLineIds, setSelectedLineIds] = useState([])
  const selectedLines = pickerLines.filter((l) => selectedLineIds.includes(l.id))
  const lineAmount = (l) => Number(l.amount) || (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)
  const selectedLinesTotal = selectedLines.reduce((sum, l) => sum + lineAmount(l), 0)
  const toggleLine = (lineId, checked) =>
    setSelectedLineIds((prev) => (checked ? [...prev, lineId] : prev.filter((x) => x !== lineId)))
  // Order-for lines belong to a month (`period`): group them so a whole month can be
  // ticked at once. Lines without one stay a single flat group.
  const lineGroups = useMemo(() => {
    const byKey = new Map()
    pickerLines.forEach((l) => {
      const label = linePeriodLabel(l)
      const key = l.period?.key || label || ""
      if (!byKey.has(key)) byKey.set(key, { key, label, status: l.period?.budgetStatus, lines: [] })
      byKey.get(key).lines.push(l)
    })
    return [...byKey.values()].sort(
      (a, b) => (a.key === "UNALLOCATED") - (b.key === "UNALLOCATED") || a.key.localeCompare(b.key)
    )
  }, [pickerLines])
  const setGroupSelected = (group, checked) => {
    const ids = group.lines.map((l) => l.id)
    setSelectedLineIds((prev) => (checked ? [...new Set([...prev, ...ids])] : prev.filter((x) => !ids.includes(x))))
  }

  // ── Whichever source is currently active ────────────────────────────────────
  const source =
    sourceMode === "direct"
      ? {
          kind: SOURCE.DIRECT,
          label: directState.title || "Direct spend",
          total: computeLinesTotal(directState.lines),
        }
      : selectedItem
      ? {
          kind: selectedItem.type === "group" ? SOURCE.GROUP : SOURCE.ORDER,
          id: selectedItem.id,
          label: selectedItem.code,
          total: hasLinePicker
            ? selectedLinesTotal
            : selectedItem.type === "group" && selectedItem.partiallyApproved
            ? approvedMembersTotal(selectedItem)
            : Number(selectedItem.total) || 0,
          partiallyApproved: selectedItem.type === "group" && selectedItem.partiallyApproved,
          detaching: selectedItem.type === "group" ? detachingMembers(selectedItem) : [],
        }
      : null

  // ── Vendor proposals for the active source ──────────────────────────────────
  // The BE filter only supports orderRequestId — a group source has to fetch
  // every proposal (no filter) and match `groupOrderId` client-side.
  const { data: allProposalsForOrder } = useGetVendorProposalsQuery(
    source?.kind === SOURCE.ORDER ? source.id : undefined,
    { skip: !source || source.kind !== SOURCE.ORDER }
  )
  const { data: allProposalsUnfiltered } = useGetVendorProposalsQuery(undefined, {
    skip: !source || source.kind !== SOURCE.GROUP,
  })
  const proposalsReady =
    source?.kind === SOURCE.ORDER
      ? allProposalsForOrder !== undefined
      : source?.kind === SOURCE.GROUP
      ? allProposalsUnfiltered !== undefined
      : false
  const proposals = useMemo(() => {
    if (!source) return []
    if (source.kind === SOURCE.ORDER) return allProposalsForOrder || []
    return (allProposalsUnfiltered || []).filter((p) => p.groupOrderId === source.id)
  }, [source, allProposalsForOrder, allProposalsUnfiltered])

  // ── Vendor / amount mode ─────────────────────────────────────────────────────
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  )

  const [amountMode, setAmountMode] = useState(AMOUNT_MODE.FULL)
  const [vendorId, setVendorId] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_OPTIONS[0].value)
  const [vendorProposalId, setVendorProposalId] = useState(null)

  // "+ New supplier" — inline alternative to picking an existing vendorId,
  // sent as `newSupplier` on the create call instead (BE creates the
  // supplier via the same SupplierService.create() POST /suppliers uses,
  // 409 on a duplicate taxCode). Only valid in FULL amount mode — a vendor
  // proposal is always tied to an existing supplier already, so picking one
  // (pickProposal below) forces this back to "existing".
  const [vendorMode, setVendorMode] = useState("existing") // "existing" | "new"
  const [newSupplier, setNewSupplier] = useState({ name: "", taxCode: "", email: "" })
  const setNewSupplierField = (k, v) => setNewSupplier((p) => ({ ...p, [k]: v }))

  // Field-format errors (lengths, MST, email) — hidden until the first create attempt.
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const newSupplierErrors =
    vendorMode === "new" ? validateFields(newSupplier, RULES.supplier) : {}
  const checkFields = () => {
    setShowFieldErrors(true)
    const bad = (sourceMode === "direct" && hasDirectSourceErrors(directSourceErrors(directState))) || hasErrors(newSupplierErrors)
    if (bad) showToastError("Một số trường chưa hợp lệ — kiểm tra các ô báo đỏ.")
    return !bad
  }

  // Reset the picked amount/proposal/vendor whenever the source changes so a
  // stale pick from a different order/group can't be submitted by mistake.
  // Every free line starts ticked.
  useEffect(() => {
    setAmountMode(AMOUNT_MODE.FULL)
    setVendorProposalId(null)
    setVendorId(null)
    setVendorMode("existing")
    setSelectedLineIds(pickerLines.map((l) => l.id))
  }, [source?.kind, source?.id])

  // Internal stock: the warehouse is the PO's vendor, so drop any picked quote and take the
  // full (0đ) amount — BE creates the warehouse proposal itself.
  const chooseInternalStock = () => {
    setAmountMode(AMOUNT_MODE.FULL)
    setVendorProposalId(null)
    setVendorId(null)
    setVendorMode("internal")
  }
  const canUseInternalStock = !!source && source.kind !== SOURCE.DIRECT

  const pickProposal = (p) => {
    setVendorProposalId(p.id)
    setVendorId(p.supplierId)
    setAmountMode(AMOUNT_MODE.PROPOSAL)
    setVendorMode("existing")
  }

  const selectedProposal = proposals.find((p) => p.id === vendorProposalId) || null

  // ── Vendor candidates: the proposals already selected for these lines ──────
  // One PO = one supplier. An order source's own selected proposal, plus — for
  // a group — the group's selected proposal and each member order's selected
  // proposal for the ticked lines. Grouped by supplier.
  // Old sources without a line picker: every member order counts.
  const lineOrderIds = new Set(
    hasLinePicker
      ? selectedLines.map((l) => String(l.orderRequestId ?? source?.id))
      : (selectedItem?.orders || []).map((o) => String(o.id))
  )
  const selectedSourceProposals =
    source?.kind === SOURCE.ORDER
      ? proposals.filter((p) => p.isSelected)
      : source?.kind === SOURCE.GROUP
      ? (allProposalsUnfiltered || []).filter(
          (p) =>
            p.isSelected &&
            (p.groupOrderId === source.id || (p.orderRequestId != null && lineOrderIds.has(String(p.orderRequestId))))
        )
      : []
  const vendorCandidates = useMemo(() => {
    const bySupplier = new Map()
    selectedSourceProposals.forEach((p) => {
      const entry = bySupplier.get(p.supplierId) || { supplierId: p.supplierId, supplierName: p.supplierName, proposals: [] }
      entry.proposals.push(p)
      bySupplier.set(p.supplierId, entry)
    })
    return [...bySupplier.values()]
  }, [selectedSourceProposals.map((p) => p.id).join(",")])
  // The source's own selected proposal can back the PO directly (split by
  // quote); a member order's can't, so those go through the draft below.
  const ownSelectedProposal = proposals.find((p) => p.isSelected) || null

  // ── Supplier auto-fill (reference-price-flow.md §3, §5) ─────────────────────
  // Once per source, after its proposals load:
  //   1. one supplier across the selected proposals → that supplier (its own
  //      proposal backs the PO when it's the source's; otherwise the draft
  //      below copies its quotes);
  //   2. several suppliers → popup to pick one of them;
  //   3. none, but every line is a contract-price item from ONE default vendor
  //      → that vendor (draft filled from the contract price);
  //   4. else → nothing; the vendor comes from an RFQ.
  // Runs once per source so a manual change afterwards is never overridden.
  const defaultRefVendorId = referencePriceVendorId(pickerLines)
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false)
  // Sets the PO's supplier to one of the candidates. When it's the only one
  // and the source's own selected proposal is theirs, the PO is split to that
  // proposal (BE prices it). Otherwise: full-amount mode + vendorId, and the
  // PO draft below prices the lines from that supplier's quotes.
  const chooseCandidate = (supplierId) => {
    setVendorPickerOpen(false)
    const own = ownSelectedProposal?.supplierId === supplierId && vendorCandidates.length === 1 ? ownSelectedProposal : null
    if (own) {
      pickProposal(own)
      return
    }
    setVendorProposalId(null)
    setAmountMode(AMOUNT_MODE.FULL)
    setVendorMode("existing")
    setVendorId(supplierId)
  }
  const autoAppliedFor = useRef(null)
  const sourceKey = source && source.kind !== SOURCE.DIRECT ? `${source.kind}-${source.id}` : null
  useEffect(() => {
    if (!sourceKey || !proposalsReady || autoAppliedFor.current === sourceKey) return
    // Wait for the source-change reset to tick this source's lines first.
    if (hasLinePicker && !selectedLines.length) return
    autoAppliedFor.current = sourceKey
    if (vendorCandidates.length === 1) chooseCandidate(vendorCandidates[0].supplierId)
    else if (vendorCandidates.length > 1) setVendorPickerOpen(true)
    else if (defaultRefVendorId != null) setVendorId(defaultRefVendorId)
  }, [sourceKey, proposalsReady, selectedLineIds.join(",")])

  // Proposal amount = sum of that supplier's item quotes, limited to the
  // ticked lines when there's a line picker (BE prices the PO the same way:
  // catalog lines match by itemId, off-catalog ones by orderRequestLineId).
  const selectedLineKeys = new Set(
    selectedLines.map((l) => proposalLineKey({ itemId: l.itemId, orderRequestLineId: l.id, offCatalog: l.itemId == null }))
  )
  const proposalTotalForLines = (p) =>
    hasLinePicker
      ? quotedItems(p)
          .filter((it) => selectedLineKeys.has(proposalLineKey(it)))
          .reduce((sum, it) => sum + (Number(it.proposedAmount) || 0), 0)
      : proposalTotal(p)

  // ── PO proposal draft (one supplier's prices for the ticked lines) ──────────
  // Full-amount mode with an existing vendor, when that vendor is one of the
  // selected-proposal candidates or every ticked line is a contract-price
  // item. One row per catalog item / off-catalog line: prefilled from that
  // vendor's quote in its selected proposal (unit price × ticked qty), else
  // blank — the BE fills blanks from vendor_items / standard price, and what
  // it can't fill is entered in the PO's missing-prices popup. Only the
  // non-blank rows are sent (PUT proposal-prices) after the PO is created.
  // Removing the draft sticks until the source changes.
  const [refDraft, setRefDraft] = useState(null) // { supplierId, fromProposals, rows: [{ key, itemId, orderRequestLineId, itemName, itemCode, quantity, unit, amount, priceFrom }] }
  const [refDraftDismissed, setRefDraftDismissed] = useState(false)
  useEffect(() => setRefDraftDismissed(false), [source?.kind, source?.id])
  const refLinesEligible =
    selectedLines.length > 0 && selectedLines.every((l) => l.itemId != null && l.isApplyReferencePrice === true)
  const refSupplierId = amountMode === AMOUNT_MODE.FULL && vendorMode === "existing" ? vendorId : null
  const candidateForVendor = vendorCandidates.find((c) => c.supplierId === refSupplierId) || null

  useEffect(() => {
    setRefDraft(null)
    if (refSupplierId == null || refDraftDismissed || !selectedLines.length) return
    if (!candidateForVendor && !refLinesEligible) return
    const quotes = (candidateForVendor?.proposals || []).flatMap(quotedItems)
    const quoteFor = (key) => quotes.find((it) => proposalLineKey(it) === key)
    const byKey = new Map()
    selectedLines.forEach((l) => {
      const offCatalog = l.itemId == null
      const key = proposalLineKey({ itemId: l.itemId, orderRequestLineId: l.id, offCatalog })
      const prev = byKey.get(key)
      byKey.set(key, {
        line: prev?.line || l,
        offCatalog,
        quantity: (prev?.quantity || 0) + (Number(l.quantity) || 0),
      })
    })
    const rows = [...byKey.entries()].map(([key, { line, offCatalog, quantity }]) => {
      const quote = quoteFor(key)
      const quoteQty = Number(quote?.quantity)
      const amount = !quote
        ? null
        : offCatalog || !(quoteQty > 0)
        ? Number(quote.proposedAmount)
        : (Number(quote.proposedAmount) / quoteQty) * quantity
      return {
        key,
        itemId: offCatalog ? null : line.itemId,
        orderRequestLineId: offCatalog ? line.id : undefined,
        offCatalog,
        itemName: line.itemName || line.offCatalogItemName,
        itemCode: line.itemCode,
        quantity,
        unit: line.uom || line.offCatalogUnit,
        amount: amount > 0 ? String(Math.round(amount)) : "",
        priceFrom: quote ? "proposal" : null,
      }
    })
    setRefDraft({ supplierId: refSupplierId, fromProposals: !!candidateForVendor, rows })
  }, [source?.kind, source?.id, refSupplierId, candidateForVendor?.proposals.map((p) => p.id).join(","), selectedLineIds.join(","), refDraftDismissed])

  // Picking the source's own proposal already prices the PO, so the draft is moot.
  const activeRefDraft = amountMode === AMOUNT_MODE.FULL ? refDraft : null
  const { data: orderReadiness } = useGetOrderPriceReadinessQuery(
    { id: source?.id, vendorId: refSupplierId, lineIds: selectedLineIds },
    { skip: !activeRefDraft || source?.kind !== SOURCE.ORDER }
  )
  const readinessRowByKey = new Map((orderReadiness?.rows || []).map((r) => [proposalLineKey(r), r]))
  const refDraftSupplierName =
    supplierOptions.find((o) => o.value === activeRefDraft?.supplierId)?.label || `#${activeRefDraft?.supplierId}`
  const refDraftTitle = activeRefDraft?.fromProposals ? "Báo giá theo NCC đã chọn trên Order" : "Báo giá theo giá nguyên tắc"

  // Where the vendor came from, shown under the picker.
  const vendorHint =
    amountMode === AMOUNT_MODE.PROPOSAL
      ? selectedProposal?.isSelected
        ? "Theo báo giá đã được chọn trên Order."
        : null
      : vendorMode === "existing" && candidateForVendor
      ? `Theo báo giá đã chọn trên ${candidateForVendor.proposals.length > 1 ? `${candidateForVendor.proposals.length} order` : "order"}.`
      : vendorMode === "existing" && vendorId != null && vendorId === defaultRefVendorId
      ? "Tự điền: NCC mặc định của các item giá nguyên tắc."
      : source && source.kind !== SOURCE.DIRECT && selectedLines.length > 0 && !refLinesEligible
      ? "Có item giá ước chừng / ngoài danh mục — NCC chốt sau khi có báo giá (gửi RFQ bên dưới)."
      : null
  const refDraftBlankCount = activeRefDraft ? activeRefDraft.rows.filter((r) => !(Number(r.amount) > 0)).length : 0
  const setRefDraftAmount = (key, amount) =>
    setRefDraft((d) => d && { ...d, rows: d.rows.map((r) => (r.key === key ? { ...r, amount } : r)) })

  // ── Submit ───────────────────────────────────────────────────────────────
  const [createPurchase, { isLoading: creating }] = useCreatePurchaseMutation()

  const noLinesTicked = hasLinePicker && selectedLineIds.length === 0

  const canSubmit =
    !!source &&
    !creating &&
    !noLinesTicked &&
    // "Lấy từ kho nội bộ" without a warehouse picked would create a normal PO.
    (vendorMode !== "internal" || vendorId != null) &&
    (sourceMode !== "direct" || isDirectSourceValid(directState)) &&
    // Vendor is optional at create — it follows the PO's selected proposal
    // later (approve is what requires one).
    (amountMode === AMOUNT_MODE.FULL ? !!paymentMethod : vendorProposalId != null)

  // The draft PO behind a vendor-proposal request needs only a valid source —
  // the vendor / payment method / proposal can be picked afterwards.
  const canCreateDraft =
    !!source &&
    !creating &&
    !noLinesTicked &&
    (sourceMode !== "direct" || isDirectSourceValid(directState))

  // `canSubmit` alone can't be clicked its way past — the button used to
  // just be disabled with no explanation, which looks like it does nothing.
  // This spells out what's actually missing.
  const invalidReason = () => {
    if (sourceMode === "direct") {
      const missing = []
      if (!directState.title?.trim()) missing.push("a title")
      if (!directState.budgetCategoryId) missing.push("a budget category")
      if (!directState.centerId) missing.push("a center")
      if (!(directState.lines || []).length) missing.push("at least 1 line")
      else if (!isDirectSourceValid(directState)) missing.push("a catalog item (or an item name) on every line")
      if (missing.length) return `Still needed: ${missing.join(", ")}.`
    } else if (!source) {
      return "Pick a source order/group first."
    }
    if (noLinesTicked) return "Tick at least one line for this PO."
    if (amountMode === AMOUNT_MODE.FULL) {
      if (!paymentMethod) return "Pick a payment method first."
    } else if (vendorProposalId == null) {
      return "Pick a vendor proposal first."
    }
    return "Fill in the required fields above first."
  }

  const previewTotal =
    amountMode === AMOUNT_MODE.PROPOSAL ? proposalTotalForLines(selectedProposal) : source?.total || 0

  // A draft PO created implicitly (see ensureDraftPurchase below), rather
  // than through the "Create draft PO" button — happens the first time the
  // user tries to request a vendor proposal *for this specific PO* before
  // one exists yet (POST /purchases/{id}/vendor-proposals needs a real PO
  // id). Once set, the Source/Vendor/Payment pickers above are locked (the
  // row already exists server-side with those values) and the primary
  // button just takes the user to it instead of creating a second PO.
  const [draftPurchaseId, setDraftPurchaseId] = useState(null)
  const sourceLocked = draftPurchaseId != null

  const buildCreateBody = () => ({
    orderRequestId: source.kind === SOURCE.ORDER ? source.id : undefined,
    groupOrderId: source.kind === SOURCE.GROUP ? source.id : undefined,
    directOrder: source.kind === SOURCE.DIRECT ? buildDirectSourcePayload(directState) : undefined,
    vendorId: vendorMode === "new" ? undefined : vendorId ?? undefined,
    newSupplier:
      vendorMode === "new" && newSupplier.name.trim() && newSupplier.taxCode.trim()
        ? {
            name: newSupplier.name.trim(),
            taxCode: newSupplier.taxCode.trim(),
            email: newSupplier.email.trim() || undefined,
          }
        : undefined,
    // Internal transfer has no payment, so it carries no payment method.
    paymentMethod: amountMode === AMOUNT_MODE.FULL && vendorMode !== "internal" ? paymentMethod : undefined,
    vendorProposalId: amountMode === AMOUNT_MODE.PROPOSAL ? vendorProposalId : undefined,
    lineIds: hasLinePicker ? selectedLineIds : undefined,
  })

  // Creating a PO from a group now auto-detaches its unapproved members
  // first (see buildCreateBody's groupOrderId branch, server-side). If that
  // leaves exactly 1 approved member, the group dissolves ENTIRELY — that
  // lone order is detached too instead of a PO being made from it — and the
  // create call itself 409s, telling the caller to retry with
  // orderRequestId. The group is gone either way by the time we see this
  // error, so clear the stale selection and point the user at the picker
  // (which will show the now-standalone order once the list refetches, via
  // createPurchase's invalidatesTags — the tags fire on failure too).
  const createPurchaseErrorMessage = (e) => {
    if (e?.status === 400) {
      return "This source can't be turned into a PO — check that it has at least one approved order and (for a group) that every order shares the same category + region."
    }
    if (e?.status === 409 && source?.kind === SOURCE.GROUP) {
      return "Couldn't create the PO from this group — it may have had only 1 approved order left, which is now a standalone order instead. Pick it directly from the list below and try again."
    }
    if (e?.status === 409 && source?.kind === SOURCE.DIRECT) {
      return "Couldn't create the PO — check that the category (and every item picked in a line) still allows a PO without an Order, and that there's enough budget."
    }
    return "Couldn't create the PO. Please try again."
  }

  // Writes the draft's non-blank prices onto the new PO's selected proposal
  // (PUT proposal-prices creates it for the PO's vendor if the create didn't).
  // Blank rows are left to the BE auto-fill / the PO's missing-prices popup.
  const [savePoPrices] = useUpdatePurchaseProposalPricesMutation()
  const saveDraftPrices = async (po) => {
    const items = (activeRefDraft?.rows || [])
      .filter((r) => Number(r.amount) > 0)
      .map((r) => ({ ...proposalLineRef(r), proposedAmount: Number(r.amount) }))
    if (!items.length || po?.id == null) return
    try {
      await savePoPrices({ id: po.id, items }).unwrap()
      setRefDraft(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Chưa lưu được giá cho PO — nhập lại trong PO."))
    }
  }

  const handleCreate = async () => {
    if (sourceLocked) {
      // The draft PO already exists (created silently while requesting a
      // vendor proposal below) — take the user to the same form-style Edit
      // screen (EditPurchaseContainer), not the read-only detail page, so
      // they land back in a "create"-like UI and can keep working (vendor/
      // payment method, more vendor proposals) rather than a dead end.
      navigate(`/procurement/purchases/${draftPurchaseId}/edit`)
      return
    }
    if (!canSubmit) {
      showToastError(invalidReason())
      return
    }
    if (!checkFields()) return
    try {
      const po = await createPurchase(buildCreateBody()).unwrap()
      showToastSuccess("Draft PO created. Submit it for approval when ready.")
      await saveDraftPrices(po)
      if (po?.detachedOrders?.length) {
        showToastSuccess(
          `${po.detachedOrders.map((o) => o.code).join(", ")} ${
            po.detachedOrders.length === 1 ? "wasn't" : "weren't"
          } approved yet and split off the group to keep going through approval on its own.`
        )
      }
      const poId = po?.id ?? po?.poCode
      navigate(poId ? `/procurement/purchases/${poId}` : "/procurement/purchases", { state: { checkPrices: true } })
    } catch (e) {
      showToastError(errorMessage(e, createPurchaseErrorMessage(e)))
      if (e?.status === 409 && source?.kind === SOURCE.GROUP) setSelectedKey(null)
    }
  }

  // Passed to VendorProposalsPanel as `resolveSourceId`: creates the draft PO
  // on first use (same body `handleCreate` would send) instead of making the
  // user click "Create draft PO" before they can request a vendor proposal.
  // Idempotent — once `draftPurchaseId` is set, later calls just return it.
  //
  // Called from two places that can legitimately race: the panel fires it in
  // the background the instant its modal/form opens (to get the item
  // checklist loading early) AND awaits it again on actual submit. Without
  // de-duping, both could see `draftPurchaseId` still null and each call
  // `createPurchase`, creating two draft POs. `draftPurchasePromiseRef` makes
  // every caller share the one in-flight request.
  const draftPurchasePromiseRef = useRef(null)
  const ensureDraftPurchase = (openProposal) => {
    if (draftPurchaseId) return Promise.resolve(draftPurchaseId)
    if (draftPurchasePromiseRef.current) return draftPurchasePromiseRef.current
    if (!canCreateDraft) {
      showToastError("Finish the source order details first.")
      return Promise.resolve(null)
    }
    if (!checkFields()) return Promise.resolve(null)
    const promise = (async () => {
      try {
        const po = await createPurchase(buildCreateBody()).unwrap()
        const id = po?.id ?? po?.poCode
        setDraftPurchaseId(id)
        showToastSuccess("Draft PO created so this vendor proposal can be attached to it.")
        await saveDraftPrices(po)
        if (openProposal) {
          navigate(`/procurement/purchases/${id}/edit`, { replace: true, state: { openProposal } })
        }
        if (po?.detachedOrders?.length) {
          showToastSuccess(
            `${po.detachedOrders.map((o) => o.code).join(", ")} ${
              po.detachedOrders.length === 1 ? "wasn't" : "weren't"
            } approved yet and split off the group to keep going through approval on its own.`
          )
        }
        return id
      } catch (e) {
        showToastError(errorMessage(e, createPurchaseErrorMessage(e)))
        if (e?.status === 409 && source?.kind === SOURCE.GROUP) setSelectedKey(null)
        return null
      } finally {
        draftPurchasePromiseRef.current = null
      }
    })()
    draftPurchasePromiseRef.current = promise
    return promise
  }

  // ── Vendor proposals FOR this PO (RFQ scoped to the PO's own id, not the
  // source order's) — separate supplier directory + item source from the
  // "pick an existing proposal" section above, matching the OR page's
  // pattern (VendorProposalsPageContainer).
  const { data: rfqSuppliers } = useGetProcurementSuppliersQuery({ isActive: true })
  const [createSupplier, { isLoading: creatingSupplier }] = useCreateProcurementSupplierMutation()
  // Same cache-lag workaround as the OR page: a just-created supplier isn't
  // guaranteed to be in `rfqSuppliers` until its list query refetches.
  const [createdSuppliers, setCreatedSuppliers] = useState([])
  const rfqSupplierOptions = useMemo(() => {
    const byId = new Map((rfqSuppliers || []).map((s) => [s.id, s]))
    createdSuppliers.forEach((s) => {
      if (!byId.has(s.id)) byId.set(s.id, s)
    })
    return Array.from(byId.values()).map((s) => ({
      value: s.id,
      label: s.name,
      taxCode: s.taxCode,
      email: s.email,
    }))
  }, [rfqSuppliers, createdSuppliers])

  const handleCreateSupplier = async (draft) => {
    try {
      const created = await createSupplier({
        name: draft.name.trim(),
        taxCode: draft.taxCode.trim(),
        email: draft.email.trim() || undefined,
      }).unwrap()
      setCreatedSuppliers((prev) => [...prev, created])
      showToastSuccess("Supplier created.")
      return created
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this supplier. Please try again."))
      throw e
    }
  }

  // "Items to propose" for the PO-scoped RFQ come from the same public
  // endpoint the supplier's own quote page uses (GET .../purchases/{id}/lines)
  // — it's the one place group-order lines are already combined (summed by
  // itemId) server-side, same as the real PO would show. Skipped until a
  // draft PO id exists.
  const { data: purchaseLinesData, isFetching: purchaseItemsLoading } = useGetVendorProposalPurchaseLinesQuery(
    { purchaseId: draftPurchaseId },
    { skip: !draftPurchaseId }
  )
  const purchaseItems = useMemo(
    () =>
      toProposalLines(purchaseLinesData?.lines),
    [purchaseLinesData]
  )

  const back = () => navigate("/procurement/purchases")

  return (
    <Page
      className="pc-page--refined pc-page--detail"
      breadcrumb={["Procurement", { label: "Purchases", to: "/procurement/purchases" }, "Create Purchase"]}
      title="Create Purchase Order"
      subtitle="Pick exactly one source — a single approved order, a group order, or (for categories that allow it) build one directly with no Order — then either take its full amount or split it to one vendor proposal's quote."
      actions={
        <Button variant="secondary" onClick={back}>
          Cancel
        </Button>
      }
    >
        <div className="pc-review">
          <div className="pc-review-main">
            <SectionCard
              icon="inventory_2"
              title="Source"
              subtitle={
                sourceLocked
                  ? "Fixed — a draft PO already exists for this source"
                  : "Choose one approved order or one group order"
              }
            >
              {sourceLocked && (
                <MDBox p={1.5} mb={1.5} borderRadius="lg" bgColor="info-light">
                  <MDTypography variant="caption" color="text">
                    A draft PO was created for this source while requesting a vendor proposal below.
                    Source/vendor/payment method are now fixed — edit them from the PO&apos;s own page
                    instead.
                  </MDTypography>
                </MDBox>
              )}
              {!sourceLocked && (
                <MDBox display="flex" gap={1} mb={2}>
                  <MDButton
                    size="small"
                    variant={sourceMode === "picker" ? "gradient" : "outlined"}
                    color="info"
                    onClick={() => setSourceMode("picker")}
                  >
                    From Order / Group
                  </MDButton>
                  <MDButton
                    size="small"
                    variant={sourceMode === "direct" ? "gradient" : "outlined"}
                    color="info"
                    onClick={() => setSourceMode("direct")}
                  >
                    Direct — No Order
                  </MDButton>
                </MDBox>
              )}
              {sourceMode === "direct" ? (
                <DirectSourceBuilder
                  mode="order"
                  value={directState}
                  onChange={setDirectState}
                  disabled={sourceLocked}
                  showErrors={showFieldErrors}
                />
              ) : itemsFetching ? (
                <MDBox display="flex" alignItems="center" gap={1.5} py={2} role="status">
                  <CircularProgress size={20} color="info" />
                  <MDTypography variant="body2" color="text">
                    Loading approved orders and group orders…
                  </MDTypography>
                </MDBox>
              ) : items.length === 0 ? (
                <MDBox textAlign="center" py={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={1}>
                    No approved orders or group orders available
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    Orders must be approved before they can be turned into a PO.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {items.map((it) => {
                    const key = `${it.type}-${it.id}`
                    const checked = selectedKey === key
                    const isGroup = it.type === "group"
                    const expanded = expandedGroupKey === key
                    const hasPurchase = it.purchases?.length > 0
                    return (
                      <MDBox
                        key={key}
                        borderRadius="lg"
                        bgColor={hasPurchase && !checked ? "grey-100" : undefined}
                        sx={{
                          border: "1px solid",
                          borderColor: checked ? "info.main" : "grey.200",
                          ...(it.overBudget && !checked && !expanded && { bgcolor: "#fff8e1" }),
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          p={1.5}
                          sx={{ cursor: sourceLocked ? "default" : "pointer" }}
                          onClick={() => !sourceLocked && setSelectedKey(key)}
                        >
                          <Radio
                            checked={checked}
                            disabled={sourceLocked}
                            onChange={() => !sourceLocked && setSelectedKey(key)}
                          />
                          {isGroup && (
                            <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                              layers
                            </Icon>
                          )}
                          <MDBox flex={1} minWidth={0}>
                            <MDTypography variant="button" fontWeight="medium">
                              {it.code} · {it.title || "—"}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {[it.categoryName, it.region, formatDate(it.createdAt)]
                                .filter(Boolean)
                                .join(" · ")}
                              {isGroup && ` · ${(it.orders || []).length} order(s)`}
                              {hasPurchase && ` · ${it.purchases.length} PO already`}
                            </MDTypography>
                            {isGroup && it.partiallyApproved && (
                              <MDTypography variant="caption" color="warning" fontWeight="bold" display="block">
                                Approved một phần — chỉ order đã duyệt được đưa vào PO
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDTypography variant="button" fontWeight="bold">
                            {formatVND(isGroup && it.partiallyApproved ? approvedMembersTotal(it) : it.total)}
                          </MDTypography>
                          {isGroup && (
                            <MDButton
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpandedGroupKey(expanded ? null : key)
                              }}
                            >
                              {expanded ? "Hide" : "Details"}
                            </MDButton>
                          )}
                        </MDBox>

                        {isGroup && expanded && (
                          <MDBox px={1.5} pb={1.5} pl={6}>
                            <MDBox display="flex" flexDirection="column" gap={0.5}>
                              {(it.orders || []).map((o) => (
                                <MDBox key={o.id} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                  <MDTypography variant="caption" color="text">
                                    {o.code} · {o.title}
                                    {it.partiallyApproved && o.status !== ORDER_STATUS.APPROVED && (
                                      <MDTypography
                                        component="span"
                                        variant="caption"
                                        color="warning"
                                        fontWeight="bold"
                                      >
                                        {" "}
                                        ({o.status})
                                      </MDTypography>
                                    )}
                                  </MDTypography>
                                  <MDTypography variant="caption" fontWeight="medium">
                                    {formatVND(o.totalAmount)}
                                  </MDTypography>
                                </MDBox>
                              ))}
                            </MDBox>
                          </MDBox>
                        )}
                      </MDBox>
                    )
                  })}
                </MDBox>
              )}
            </SectionCard>

            {hasLinePicker && (
              <SectionCard
                icon="checklist"
                title="Lines for this PO"
                subtitle={`${selectedLines.length}/${pickerLines.length} line(s) · ${formatVND(selectedLinesTotal)}`}
              >
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  <MDBox pb={0.75} sx={{ borderBottom: "1px solid", borderColor: "grey.200" }}>
                    <Checkbox
                      checked={selectedLines.length === pickerLines.length}
                      disabled={sourceLocked}
                      onChange={(v) => setSelectedLineIds(v ? pickerLines.map((l) => l.id) : [])}
                      label="Select all"
                    />
                  </MDBox>
                  {lineGroups.map((group) => {
                    const ticked = group.lines.filter((l) => selectedLineIds.includes(l.id)).length
                    return (
                      <MDBox key={group.key || "all"} display="flex" flexDirection="column" gap={0.5}>
                        {group.label && (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            pt={0.5}
                            sx={{ borderBottom: "1px dashed", borderColor: "grey.300" }}
                          >
                            <Checkbox
                              checked={ticked === group.lines.length}
                              disabled={sourceLocked}
                              onChange={(v) => setGroupSelected(group, v)}
                              label={`${group.label} — chọn cả tháng`}
                            />
                            <MDTypography
                              variant="caption"
                              sx={{ whiteSpace: "nowrap", color: LINE_PERIOD_COLOR[group.status] || "text.secondary" }}
                            >
                              {ticked}/{group.lines.length} line(s) ·{" "}
                              <strong>{formatVND(group.lines.reduce((sum, l) => sum + lineAmount(l), 0))}</strong>
                            </MDTypography>
                          </MDBox>
                        )}
                        {group.lines.map((l) => {
                          const orCode =
                            source?.kind === SOURCE.GROUP
                              ? (selectedItem?.orders || []).find((o) => String(o.id) === String(l.orderRequestId))?.code
                              : null
                          return (
                            <MDBox key={l.id} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                              <Checkbox
                                checked={selectedLineIds.includes(l.id)}
                                disabled={sourceLocked}
                                onChange={(v) => toggleLine(l.id, v)}
                                label={[l.itemName || l.offCatalogItemName || `Line #${l.id}`, orCode]
                                  .filter(Boolean)
                                  .join(" · ")}
                              />
                              <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
                                {Number(l.quantity) || 0} × {formatVND(l.unitPrice)} = <strong>{formatVND(lineAmount(l))}</strong>
                              </MDTypography>
                            </MDBox>
                          )
                        })}
                      </MDBox>
                    )
                  })}
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    pt={0.75}
                    sx={{ borderTop: "1px solid", borderColor: "grey.200" }}
                  >
                    <MDTypography variant="caption" color="text">
                      Order total ({pickerLines.length} line(s)):{" "}
                      <strong>{formatVND(pickerLines.reduce((sum, l) => sum + lineAmount(l), 0))}</strong>
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Selected ({selectedLines.length}): <strong>{formatVND(selectedLinesTotal)}</strong>
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" mt={1}>
                    Lines left out stay available for another PO (e.g. from a different supplier).
                  </MDTypography>
                </MDBox>
              </SectionCard>
            )}

            <SectionCard
              icon="request_quote"
              title="Vendor proposals"
              subtitle={source ? "Pick a quote to split the PO to that amount, or skip to use the full source total" : "Choose a source first"}
            >
              {!source ? (
                <MDTypography variant="body2" color="text">
                  Select an order or group order above to see and add vendor proposals.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {proposals.length === 0 ? (
                    <MDTypography variant="body2" color="text">
                      No vendor proposals yet for this source.
                    </MDTypography>
                  ) : (
                    <MDBox display="flex" flexDirection="column" gap={0.75}>
                      {proposals.map((p) => {
                        const picked = vendorProposalId === p.id
                        return (
                          <MDBox
                            key={p.id}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                            p={1}
                            borderRadius="md"
                            bgColor="grey-100"
                            sx={{
                              border: "1px solid",
                              borderColor: picked ? "info.main" : "grey.200",
                              cursor: sourceLocked ? "default" : "pointer",
                              opacity: sourceLocked && !picked ? 0.6 : 1,
                            }}
                            onClick={() => !sourceLocked && pickProposal(p)}
                          >
                            <MDBox minWidth={0}>
                              <MDTypography variant="caption" fontWeight="medium" display="block">
                                {p.supplierName}
                                {p.isSelected && (
                                  <MDTypography component="span" variant="caption" color="success" ml={0.5}>
                                    · selected
                                  </MDTypography>
                                )}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                {quotedItems(p).length} item{quotedItems(p).length === 1 ? "" : "s"} quoted
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography variant="caption" fontWeight="bold">
                                {hasQuote(p) ? formatVND(proposalTotalForLines(p)) : NO_QUOTE_LABEL}
                              </MDTypography>
                              {picked && (
                                <Icon color="info" fontSize="small">
                                  check_circle
                                </Icon>
                              )}
                            </MDBox>
                          </MDBox>
                        )
                      })}
                    </MDBox>
                  )}

                  {vendorProposalId && !sourceLocked && (
                    <MDButton
                      size="small"
                      variant="text"
                      color="secondary"
                      sx={{ alignSelf: "flex-start" }}
                      onClick={() => {
                        setVendorProposalId(null)
                        setAmountMode(AMOUNT_MODE.FULL)
                        setVendorId(null)
                      }}
                    >
                      Clear selection — use full amount instead
                    </MDButton>
                  )}

                  <MDTypography variant="caption" color="text">
                    {sourceLocked
                      ? "This selection is now fixed — the draft PO already exists with it."
                      : "To add a vendor proposal here, go to the order request's own page and use its Vendor Proposals section. Or use \"Request proposal from vendor\" below to send one scoped to this PO directly."}
                  </MDTypography>
                </MDBox>
              )}
            </SectionCard>

            {activeRefDraft && !sourceLocked && (
              <SectionCard
                icon="price_check"
                title={refDraftTitle}
                subtitle={`Giá của ${refDraftSupplierName} cho các line đã chọn — lưu vào PO khi tạo. Ô trống sẽ được hệ thống tự điền; dòng nào vẫn thiếu giá thì nhập trên PO. Có thể sửa, hoặc xoá bảng này.`}
              >
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {activeRefDraft.rows.map((r) => {
                    const readinessRow = readinessRowByKey.get(r.key)
                    const missing = !(Number(r.amount) > 0) && readinessRow?.status === "MISSING"
                    return (
                      <MDBox key={r.key} display="flex" alignItems="center" gap={2}>
                        <MDBox flex={1} minWidth={0}>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {r.itemName}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {[
                              r.itemCode,
                              `SL ${r.quantity ?? "—"}${r.unit ? ` ${r.unit}` : ""}`,
                              Number(r.amount) > 0 && r.priceFrom !== "proposal" ? null : draftRowHint(r, readinessRow),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </MDTypography>
                        </MDBox>
                        <MDBox width={220}>
                          <MDNumberInput
                            fullWidth
                            placeholder={
                              readinessRow?.suggestedAmount != null
                                ? formatNumberInput(Math.round(readinessRow.suggestedAmount))
                                : "Báo giá cho cả SL (VND)"
                            }
                            value={r.amount}
                            error={missing}
                            onChange={(v) => setRefDraftAmount(r.key, v)}
                          />
                        </MDBox>
                      </MDBox>
                    )
                  })}
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <MDTypography variant="caption" color="text">
                      {`Tổng đã nhập: ${formatVND(activeRefDraft.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0))}`}
                      {refDraftBlankCount > 0 && ` · ${refDraftBlankCount} dòng để trống`}
                    </MDTypography>
                    <MDButton size="small" variant="text" color="error" onClick={() => setRefDraftDismissed(true)}>
                      <Icon sx={{ mr: 0.5 }}>delete</Icon>
                      Xoá bảng giá
                    </MDButton>
                  </MDBox>
                </MDBox>
              </SectionCard>
            )}

            <SectionCard
              icon="mail"
              title="Request proposal from vendor"
              subtitle={
                source
                  ? "Send an RFQ scoped to this PO's own id, independent of the source order's proposals above"
                  : "Choose a source first"
              }
            >
              {!source ? (
                <MDTypography variant="body2" color="text">
                  Select an order or group order above first.
                </MDTypography>
              ) : (
                <>
                  {!draftPurchaseId && (
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                      Requesting a proposal here silently creates the draft PO first (vendor and
                      payment method can be picked afterwards), so the request can be attached to it
                      right away.
                    </MDTypography>
                  )}
                  {/* VendorProposalsPanel (and the CreateProposalDrawer modal it
                      opens) is built on the procurement/ui "pc-*" design
                      system — every pc-btn/pc-control/etc. color comes from
                      CSS custom properties that only exist inside a `.pc-root`
                      ancestor (see ui/tokens.css). This page is Material
                      Dashboard, not that design system's own <Page> shell, so
                      without this wrapper every pc-* var resolves to nothing
                      and things like the primary button render white-on-white.
                      `.pc-root` also sets page-shell assumptions (full-height
                      canvas background) that don't apply to an embedded
                      section — neutralize those two inline while still
                      inheriting the color/spacing/font variables. */}
                  <div className="pc-root" style={{ minHeight: "auto", background: "transparent" }}>
                    <VendorProposalsPanel
                      sourceType="purchase"
                      sourceId={draftPurchaseId}
                      resolveSourceId={ensureDraftPurchase}
                      orderLines={purchaseItems}
                      itemsLoading={purchaseItemsLoading}
                      supplierOptions={rfqSupplierOptions}
                      onCreateSupplier={handleCreateSupplier}
                      creatingSupplier={creatingSupplier}
                    />
                  </div>
                </>
              )}
            </SectionCard>
          </div>

          <aside className="pc-review-side">
            <MDBox>
              <SectionCard icon="receipt_long" title="PO summary" mb={0}>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                    <MDTypography variant="caption" color="text" display="block">
                      Source
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium">
                      {source
                        ? `${
                            source.kind === SOURCE.GROUP
                              ? "Group Order"
                              : source.kind === SOURCE.DIRECT
                              ? "Direct — no Order"
                              : "Order"
                          } · ${source.label}`
                        : "None selected"}
                    </MDTypography>
                  </MDBox>

                  {amountMode === AMOUNT_MODE.FULL ? (
                    <>
                      {vendorMode === "internal" ? (
                        <InternalStockChoice
                          params={
                            source
                              ? {
                                  [source.kind === SOURCE.GROUP ? "groupOrderId" : "orderRequestId"]: source.id,
                                  lineIds: hasLinePicker ? selectedLineIds : undefined,
                                }
                              : null
                          }
                          value={vendorId}
                          onChange={setVendorId}
                          disabled={sourceLocked}
                        />
                      ) : vendorMode === "new" ? (
                        <MDBox display="flex" flexDirection="column" gap={1.5} mb={1.5}>
                          <MDInput
                            fullWidth
                            required
                            labelType="non-sticky"
                            labelText="Supplier name"
                            value={newSupplier.name}
                            error={showFieldErrors && !!newSupplierErrors.name}
                            helperText={showFieldErrors ? newSupplierErrors.name : undefined}
                            onChange={(e) => setNewSupplierField("name", e.target.value)}
                            disabled={sourceLocked}
                          />
                          <MDInput
                            fullWidth
                            required
                            labelType="non-sticky"
                            labelText="Tax code"
                            value={newSupplier.taxCode}
                            error={showFieldErrors && !!newSupplierErrors.taxCode}
                            helperText={showFieldErrors ? newSupplierErrors.taxCode : undefined}
                            onChange={(e) => setNewSupplierField("taxCode", e.target.value)}
                            disabled={sourceLocked}
                          />
                          <MDInput
                            fullWidth
                            labelType="non-sticky"
                            labelText="Email"
                            value={newSupplier.email}
                            error={showFieldErrors && !!newSupplierErrors.email}
                            helperText={showFieldErrors ? newSupplierErrors.email : undefined}
                            onChange={(e) => setNewSupplierField("email", e.target.value)}
                            disabled={sourceLocked}
                          />
                        </MDBox>
                      ) : (
                        <MDSelect
                          labelType="non-sticky"
                          labelText="Vendor"
                          placeholder="Select supplier…"
                          options={supplierOptions.filter((o) => !isInternalStockSupplier(o.value))}
                          value={supplierOptions.find((o) => o.value === vendorId) || null}
                          onChange={(o) => setVendorId(o?.value ?? null)}
                          isDisabled={sourceLocked}
                        />
                      )}
                      {!sourceLocked && (
                        <MDBox display="flex" gap={1} flexWrap="wrap" mt={-0.5}>
                          {vendorMode !== "existing" && (
                            <MDButton size="small" variant="text" color="info" sx={{ px: 0 }} onClick={() => setVendorMode("existing")}>
                              ← Choose a supplier
                            </MDButton>
                          )}
                          {vendorMode !== "new" && (
                            <MDButton size="small" variant="text" color="info" sx={{ px: 0 }} onClick={() => setVendorMode("new")}>
                              + New supplier
                            </MDButton>
                          )}
                          {vendorMode !== "internal" && canUseInternalStock && (
                            <MDButton size="small" variant="outlined" color="info" onClick={chooseInternalStock}>
                              Use internal stock
                            </MDButton>
                          )}
                        </MDBox>
                      )}
                      {sourceKey && !proposalsReady && (
                        <MDBox display="flex" alignItems="center" gap={1} mt={-0.5} role="status">
                          <CircularProgress size={14} color="info" />
                          <MDTypography variant="caption" color="text">
                            Đang tìm nhà cung cấp từ báo giá đã chọn…
                          </MDTypography>
                        </MDBox>
                      )}
                      {vendorHint && (
                        <MDTypography variant="caption" color="text" display="block" mt={-0.5}>
                          {vendorHint}
                        </MDTypography>
                      )}
                      {vendorCandidates.length > 1 && !sourceLocked && (
                        <MDButton
                          size="small"
                          variant="text"
                          color="info"
                          sx={{ alignSelf: "flex-start", px: 0 }}
                          onClick={() => setVendorPickerOpen(true)}
                        >
                          Chọn NCC từ báo giá đã chọn ({vendorCandidates.length})
                        </MDButton>
                      )}
                      {vendorMode !== "internal" && (
                      <MDSelect
                        labelType="non-sticky"
                        labelText="Payment method"
                        options={PAYMENT_METHOD_OPTIONS}
                        value={PAYMENT_METHOD_OPTIONS.find((o) => o.value === paymentMethod) || null}
                        onChange={(o) => setPaymentMethod(o?.value ?? PAYMENT_METHOD_OPTIONS[0].value)}
                        isDisabled={sourceLocked}
                      />
                      )}
                    </>
                  ) : (
                    <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                      <MDTypography variant="caption" color="text" display="block">
                        Vendor (from proposal)
                      </MDTypography>
                      <MDTypography variant="button" fontWeight="medium">
                        {selectedProposal?.supplierName || "—"}
                      </MDTypography>
                      {vendorHint && (
                        <MDTypography variant="caption" color="text" display="block">
                          {vendorHint}
                        </MDTypography>
                      )}
                      {canUseInternalStock && !sourceLocked && (
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ mt: 1 }}
                          onClick={chooseInternalStock}
                        >
                          Use internal stock
                        </MDButton>
                      )}
                    </MDBox>
                  )}

                  {source?.partiallyApproved && (
                    <MDBox p={1.5} borderRadius="lg" bgColor="warning" variant="gradient" opacity={0.9}>
                      <MDTypography variant="caption" color="white" fontWeight="bold" display="block">
                        Chỉ các order đã duyệt được đưa vào PO. Các order sau sẽ bị tách khỏi nhóm khi
                        tạo PO:
                      </MDTypography>
                      {source.detaching.map((o) => (
                        <MDTypography key={o.id} variant="caption" color="white" display="block">
                          {o.code} ({o.status})
                        </MDTypography>
                      ))}
                    </MDBox>
                  )}

                  <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="button" fontWeight="medium">
                        TOTAL PO
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold">
                        {formatVND(previewTotal)}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text">
                      {amountMode === AMOUNT_MODE.PROPOSAL
                        ? "Split to the selected vendor proposal's amount."
                        : source?.partiallyApproved
                        ? "Sum of only the approved orders in this group."
                        : source?.kind === SOURCE.DIRECT
                        ? "Sum of the direct spend lines."
                        : "Full amount of the selected source."}
                    </MDTypography>
                  </MDBox>

                  {/* Not gated on `canSubmit` — an invalid click still needs
                      to fire, so handleCreate can toast what's missing
                      instead of the button just doing nothing. */}
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    disabled={creating}
                    onClick={handleCreate}
                  >
                    {sourceLocked ? "Edit draft PO →" : creating ? "Creating…" : "Create draft PO"}
                  </MDButton>
                  {source && (
                    <MDBox display="flex" justifyContent="center">
                      <StatusBadge status="draft" />
                    </MDBox>
                  )}
                </MDBox>
              </SectionCard>
            </MDBox>
          </aside>
        </div>

      {/* The ticked lines' selected proposals name more than one supplier —
          one PO takes exactly one, so the user picks it here. Coverage = how
          many ticked lines that supplier has quoted; the rest are priced in
          the PO draft (catalogue price, or typed in). */}
      <Dialog open={vendorPickerOpen} onClose={() => setVendorPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Chọn nhà cung cấp cho PO</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={1.5}>
            Các line đã chọn đang có báo giá được chọn từ nhiều NCC. Mỗi PO chỉ có 1 NCC — chọn 1 bên. Line nào
            NCC đó chưa báo giá sẽ cần nhập giá ở bước sau.
          </MDTypography>
          <MDBox display="flex" flexDirection="column" gap={1}>
            {vendorCandidates.map((c) => {
              const quotedKeys = new Set(
                c.proposals.flatMap(quotedItems).map(proposalLineKey).filter((k) => selectedLineKeys.has(k))
              )
              const orderCodes = c.proposals.map((p) =>
                p.groupOrderId != null
                  ? "Group"
                  : (selectedItem?.orders || []).find((o) => String(o.id) === String(p.orderRequestId))?.code ||
                    selectedItem?.code ||
                    `OR #${p.orderRequestId}`
              )
              return (
                <MDBox
                  key={c.supplierId}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  p={1.25}
                  borderRadius="md"
                  sx={{
                    border: "1px solid",
                    borderColor: c.supplierId === vendorId ? "info.main" : "grey.300",
                    cursor: "pointer",
                  }}
                  onClick={() => chooseCandidate(c.supplierId)}
                >
                  <MDBox minWidth={0}>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {c.supplierName}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      Đã chọn trên: {[...new Set(orderCodes)].join(", ")}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color={quotedKeys.size === selectedLineKeys.size ? "success" : "warning"}>
                    Có giá {quotedKeys.size}/{selectedLineKeys.size} dòng
                  </MDTypography>
                </MDBox>
              )
            })}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setVendorPickerOpen(false)}>
            Để sau
          </MDButton>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

// Edit a PO while it's a Draft, or answer an info_request on it — same idea
// as the Order/Payment edit forms: reopen the PUT-backed fields, bottom
// action is Resubmit at info_requested (Save changes at draft). Source
// (order/group) and vendor-proposal split are fixed at creation and aren't
// editable here — only the full-amount fields (vendor, payment method) are.
const EditPurchaseContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: po, isLoading, isError, refetch } = useGetPurchaseQuery(id, { skip: !id })
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  )

  const [vendorId, setVendorId] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_OPTIONS[0].value)
  const [seeded, setSeeded] = useState(false)

  useEffect(() => {
    if (seeded || !po) return
    setVendorId(po.vendorId ?? null)
    setPaymentMethod(po.paymentMethod || PAYMENT_METHOD_OPTIONS[0].value)
    setSeeded(true)
  }, [seeded, po])

  // Selecting a proposal makes the BE set the PO's vendor server-side; follow
  // it so Save doesn't send the stale (possibly null) vendorId back and wipe it.
  useEffect(() => {
    if (seeded && po) setVendorId(po.vendorId ?? null)
  }, [po?.vendorId])

  const [updatePurchase, { isLoading: updating }] = useUpdatePurchaseMutation()
  const [resubmitPurchase, { isLoading: resubmitting }] = useResubmitPurchaseMutation()
  const busy = updating || resubmitting

  const back = () => navigate(`/procurement/purchases/${id}`)

  const isInfoRequested = po?.status === PO_STATUS.INFO_REQUESTED
  const isEditable = canEditPurchase(po)
  // With a selected proposal the BE owns the PO's vendor (and amount) — the
  // picker is read-only then and vendorId is not sent back.
  const hasSelectedProposal = (po?.vendorProposals || []).some((p) => p.isSelected)

  // ── Vendor proposals FOR this PO — same "Request proposal from vendor"
  // panel as the create form, just simpler: the PO already has a real `id`
  // here, so no silent-create-on-first-use resolver is needed.
  const { data: rfqSuppliers } = useGetProcurementSuppliersQuery({ isActive: true })
  const [createSupplier, { isLoading: creatingSupplier }] = useCreateProcurementSupplierMutation()
  const [createdSuppliers, setCreatedSuppliers] = useState([])
  const rfqSupplierOptions = useMemo(() => {
    const byId = new Map((rfqSuppliers || []).map((s) => [s.id, s]))
    createdSuppliers.forEach((s) => {
      if (!byId.has(s.id)) byId.set(s.id, s)
    })
    return Array.from(byId.values()).map((s) => ({
      value: s.id,
      label: s.name,
      taxCode: s.taxCode,
      email: s.email,
    }))
  }, [rfqSuppliers, createdSuppliers])

  const handleCreateSupplier = async (draft) => {
    try {
      const created = await createSupplier({
        name: draft.name.trim(),
        taxCode: draft.taxCode.trim(),
        email: draft.email.trim() || undefined,
      }).unwrap()
      setCreatedSuppliers((prev) => [...prev, created])
      showToastSuccess("Supplier created.")
      return created
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this supplier. Please try again."))
      throw e
    }
  }

  const { data: purchaseLinesData, isFetching: purchaseItemsLoading } = useGetVendorProposalPurchaseLinesQuery(
    { purchaseId: id },
    { skip: !id }
  )
  const purchaseItems = useMemo(
    () =>
      toProposalLines(purchaseLinesData?.lines),
    [purchaseLinesData]
  )

  const handleSave = async () => {
    try {
      await updatePurchase({
        id,
        vendorId: hasSelectedProposal ? undefined : vendorId ?? undefined,
        paymentMethod,
      }).unwrap()
      if (isInfoRequested) {
        const result = await resubmitPurchase({ id, note: "" })
          .unwrap()
          .catch((e) => {
            // 400 = no selected proposal / unpriced lines: the PO detail's
            // price popup is where they get filled in.
            if (e?.status !== 400) throw e
            showToastError(errorMessage(e, "Couldn't resubmit this PO — some prices are missing."))
            navigate(`/procurement/purchases/${id}`, { state: { checkPrices: true } })
            return null
          })
        if (!result) return
        // Resubmitting after the info-request deadline already passed gets
        // auto-rejected by the backend instead of going back to pending —
        // still a 200, so a green success toast would misreport that.
        if (isAutoRejectedOutcome("resubmit", result, PO_STATUS.REJECTED)) {
          showToastError(AUTO_REJECT_MESSAGE)
        } else {
          showToastSuccess("PO resubmitted for approval.")
        }
      } else {
        showToastSuccess("Purchase order updated.")
      }
      navigate(`/procurement/purchases/${id}`)
    } catch (e) {
      showToastError(errorMessage(e, "Couldn't save this PO. Please try again."))
    }
  }

  if (isLoading) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={["Procurement", "Purchases", "Edit"]} title="Edit Purchase Order">
        <Loading />
      </Page>
    )
  }

  if (isError || !po || !isEditable) {
    return (
      <Page className="pc-page--refined pc-page--detail" breadcrumb={["Procurement", "Purchases", "Edit"]} title="Edit Purchase Order">
        <Card>
          <div className="pc-section-empty">
            <div className="pc-section-empty-title">
              {isError || !po ? "Couldn't load this PO" : "This PO is no longer editable"}
            </div>
            <Button variant="secondary" size="sm" onClick={isError ? refetch : back}>
              {isError && po == null ? "Retry" : "View details"}
            </Button>
          </div>
        </Card>
      </Page>
    )
  }

  return (
    <Page
      className="pc-page--refined pc-page--detail"
      breadcrumb={[
        "Procurement",
        { label: "Purchases", to: "/procurement/purchases" },
        { label: po.poCode || "PO", to: `/procurement/purchases/${id}` },
        "Edit",
      ]}
      title={
        <span className="pc-title-row">
          {isInfoRequested ? "Edit & Resubmit" : "Edit"} Purchase Order
          <StatusBadge status={po.status} />
        </span>
      }
      actions={
        <Button variant="secondary" onClick={back}>
          Cancel
        </Button>
      }
    >
        <div className="pc-review">
          <div className="pc-review-main">
            <SectionCard icon="inventory_2" title="Source" subtitle="Fixed at creation — can't be changed here">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text" display="block">
                    Linked order(s)
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {(po.linkedOrders || []).map((o) => o.orCode || o.code || o.id).join(", ") || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text" display="block">
                    Created
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {formatDate(po.createdAt)}
                  </MDTypography>
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard icon="storefront" title="Vendor & Payment">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {hasSelectedProposal ? (
                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block">
                        Vendor
                      </MDTypography>
                      <MDTypography variant="button" fontWeight="medium" display="block">
                        {po.supplierName || "—"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.8 }}>
                        From the selected vendor proposal. Select another proposal below to change it.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDSelect
                      labelType="non-sticky"
                      labelText="Vendor"
                      placeholder="Select supplier…"
                      options={supplierOptions}
                      value={supplierOptions.find((o) => o.value === vendorId) || null}
                      onChange={(o) => setVendorId(o?.value ?? null)}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDSelect
                    labelType="non-sticky"
                    labelText="Payment method"
                    options={PAYMENT_METHOD_OPTIONS}
                    value={PAYMENT_METHOD_OPTIONS.find((o) => o.value === paymentMethod) || null}
                    onChange={(o) => setPaymentMethod(o?.value ?? PAYMENT_METHOD_OPTIONS[0].value)}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard icon="mail" title="Request proposal from vendor">
              {/* See the matching wrapper in CreatePurchaseContainer above for
                  why this needs a `.pc-root` ancestor. */}
              <div className="pc-root" style={{ minHeight: "auto", background: "transparent" }}>
                <VendorProposalsPanel
                  sourceType="purchase"
                  sourceId={id}
                  orderProposals={po.orderProposals || []}
                  linkedOrders={po.linkedOrders}
                  autoOpen={location.state?.openProposal}
                  orderLines={purchaseItems}
                  itemsLoading={purchaseItemsLoading}
                  supplierOptions={rfqSupplierOptions}
                  onCreateSupplier={handleCreateSupplier}
                  creatingSupplier={creatingSupplier}
                />
              </div>
            </SectionCard>
          </div>

          <aside className="pc-review-side">
            <MDBox>
              <SectionCard icon="receipt_long" title="PO summary" mb={0}>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="button" fontWeight="medium">
                        TOTAL PO
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold">
                        {formatVND(po.total)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    disabled={busy}
                    onClick={handleSave}
                  >
                    {busy ? "Saving…" : isInfoRequested ? "Resubmit" : "Save changes"}
                  </MDButton>
                </MDBox>
              </SectionCard>
            </MDBox>
          </aside>
        </div>
    </Page>
  )
}

// Route wrapper: /procurement/purchases/new has no :id (create), /:id/edit
// does (edit) — pick the right screen without conditionally calling either
// one's hooks.
const PurchaseFormRouter = () => {
  const { id } = useParams()
  return id ? <EditPurchaseContainer /> : <CreatePurchaseContainer />
}

export default PurchaseFormRouter
